import React from 'react';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';

import InfoOutlineIcon from '@mui/icons-material/InfoOutlined';
import { Box, MenuItem, Select, styled, Tooltip, Typography } from '@mui/material';

const FadeInBox = styled(Box)({
  animation: 'fadeIn 0.4s ease 0.02s both',
  '@keyframes fadeIn': {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
});

interface SelectOption {
  label: string;
  value: string | number;
}

interface LabelledSelectProps<T extends FieldValues> {
  name: Path<T>;
  label: string;
  tooltip?: string;
  control: Control<T>;
  options: SelectOption[];
  width?: string | number;
  placeholder?: string;
  disabled?: boolean;
  selectSize: 'small' | 'medium';
}

const LabelledSelect = <T extends FieldValues>({
  name,
  label,
  tooltip,
  control,
  options,
  width = '100%',
  placeholder,
  disabled = false,
  selectSize = 'small',
}: LabelledSelectProps<T>) => {
  return (
    <FadeInBox display="flex" flexDirection="column" alignItems="flex-start" width={width}>
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        {label}
        {tooltip && (
          <Tooltip sx={{ pl: '2px' }} title={tooltip}>
            <InfoOutlineIcon fontSize="inherit" />
          </Tooltip>
        )}
      </Typography>

      <Controller
        name={name}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <Select
            {...field}
            displayEmpty
            disabled={disabled}
            size={selectSize}
            error={!!error}
            onChange={(e) => field.onChange(e.target.value)}
            data-testid={`${name}-select`}
            sx={{ width: '100%' }}
            renderValue={(selected) => {
              if (!selected && placeholder) {
                return <Typography color="text.secondary">{placeholder}</Typography>;
              }
              return options.find((o) => o.value === selected)?.label ?? '';
            }}>
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        )}
      />
    </FadeInBox>
  );
};

export default LabelledSelect;
